// A minimal PDF writer: pages that each show one picture, with an optional line of text at the
// foot. Enough to export the family chart without shipping a PDF library. Pictures go in as
// lossless deflated pixels (crisp text, no JPEG smudging), or as a JPEG embedded as-is.
//
// Over the picture goes an invisible copy of its text, placed where the picture shows it, the
// way scanned documents are made searchable. It's set in a tiny embedded font whose one glyph
// is blank (ttf.ts), with each character's code mapped back to Unicode, so names in any
// language can be selected, copied, and found, in Preview as well as other readers.

import { BLANK_ADVANCE, blankFont } from './ttf';

export type PageSize = 'fit' | 'letter' | 'a4';

const PAGES: Record<Exclude<PageSize, 'fit'>, [number, number]> = { letter: [612, 792], a4: [595.28, 841.89] };
/** PDF's own limit on page sides, in points. */
const MAX_PT = 14400;
/** Half an inch, in points. */
const MARGIN = 36;
/** A chart pixel at its natural size (96 per inch), in points. */
export const PT_PER_PX = 0.75;

/** JPEG pixel size, read from its SOF marker. */
export function jpegSize(bytes: Uint8Array): { width: number; height: number } {
  let i = 2;
  while (i < bytes.length) {
    if (bytes[i] !== 0xff) throw new Error('Not a JPEG');
    const marker = bytes[i + 1];
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { height: (bytes[i + 5] << 8) | bytes[i + 6], width: (bytes[i + 7] << 8) | bytes[i + 8] };
    }
    i += 2 + len;
  }
  throw new Error('JPEG size not found');
}

/** One page's share of the picture, in the picture's own pixels, and where it goes on the page, in points. */
export interface Tile {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Points per picture pixel on this page. */
  k: number;
  row: number;
  col: number;
}

export interface PagePlan {
  width: number;
  height: number;
  rows: number;
  cols: number;
  tiles: Tile[];
}

/**
 * How a `width`×`height` picture goes onto paper. `fit` makes one page the picture's size;
 * a paper size shrinks the picture onto one page; `tiled` prints it at its natural size across
 * as many pages as it needs (turned whichever way needs fewer), to trim and tape together.
 */
export function planPages(width: number, height: number, page: PageSize, tiled = false): PagePlan {
  if (page === 'fit') {
    const k = Math.min(PT_PER_PX, MAX_PT / width, MAX_PT / height);
    return { width: width * k, height: height * k, rows: 1, cols: 1, tiles: [{ sx: 0, sy: 0, sw: width, sh: height, x: 0, y: 0, w: width * k, h: height * k, k, row: 0, col: 0 }] };
  }
  const [a, b] = PAGES[page];
  if (!tiled) {
    const [pw, ph] = width > height ? [b, a] : [a, b];
    const k = Math.min((pw - MARGIN * 2) / width, (ph - MARGIN * 2) / height);
    const w = width * k;
    const h = height * k;
    return { width: pw, height: ph, rows: 1, cols: 1, tiles: [{ sx: 0, sy: 0, sw: width, sh: height, x: (pw - w) / 2, y: (ph - h) / 2, w, h, k, row: 0, col: 0 }] };
  }
  const k = PT_PER_PX;
  const grid = (pw: number, ph: number) => {
    const uw = (pw - MARGIN * 2) / k;
    const uh = (ph - MARGIN * 2) / k;
    return { pw, ph, uw, uh, cols: Math.ceil(width / uw), rows: Math.ceil(height / uh) };
  };
  const portrait = grid(a, b);
  const landscape = grid(b, a);
  const g = landscape.cols * landscape.rows <= portrait.cols * portrait.rows ? landscape : portrait;
  const tiles: Tile[] = [];
  for (let row = 0; row < g.rows; row++)
    for (let col = 0; col < g.cols; col++) {
      const sx = col * g.uw;
      const sy = row * g.uh;
      const sw = Math.min(g.uw, width - sx);
      const sh = Math.min(g.uh, height - sy);
      // Top left of the printable area, so the pieces line up when the margins are trimmed.
      tiles.push({ sx, sy, sw, sh, x: MARGIN, y: g.ph - MARGIN - sh * k, w: sw * k, h: sh * k, k, row, col });
    }
  return { width: g.pw, height: g.ph, rows: g.rows, cols: g.cols, tiles };
}

export interface PdfImage {
  width: number;
  height: number;
  /** RGB pixels, deflated (zlib), or a whole JPEG file. */
  data: Uint8Array;
  filter: 'FlateDecode' | 'DCTDecode';
  /** Transparency, one deflated byte per pixel, for pictures drawn over other things. */
  alpha?: Uint8Array;
}

export interface PdfPage {
  width: number;
  height: number;
  image: PdfImage;
  /** Where the image goes, in points from the bottom left. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** A small line of text centered at the bottom of the page. */
  footer?: string;
  /** Invisible, selectable text over the picture. */
  texts?: PdfText[];
}

/** A line of selectable text: its left edge and baseline in points from the bottom left, size, and the width it spans. */
export interface PdfText {
  x: number;
  y: number;
  size: number;
  width: number;
  text: string;
}

/** UTF-16BE hex for one character (or a few), as a ToUnicode CMap wants it. */
export function utf16Hex(ch: string): string {
  let out = '';
  for (let i = 0; i < ch.length; i++) out += ch.charCodeAt(i).toString(16).padStart(4, '0').toUpperCase();
  return out;
}

/** Maps two-byte codes back to the characters they stand for, so copying and searching get real text. */
export function toUnicodeCMap(pairs: [number, string][]): string {
  const lines = ['/CIDInit /ProcSet findresource begin', '12 dict begin', 'begincmap', '/CIDSystemInfo << /Registry (Adobe) /Ordering (UCS) /Supplement 0 >> def', '/CMapName /Adobe-Identity-UCS def', '/CMapType 2 def', '1 begincodespacerange', '<0000> <FFFF>', 'endcodespacerange'];
  for (let i = 0; i < pairs.length; i += 100) {
    const chunk = pairs.slice(i, i + 100);
    lines.push(`${chunk.length} beginbfchar`);
    for (const [code, ch] of chunk) lines.push(`<${code4(code)}> <${utf16Hex(ch)}>`);
    lines.push('endbfchar');
  }
  lines.push('endcmap', 'CMapName currentdict /CMap defineresource pop', 'end', 'end');
  return lines.join('\n');
}

export const code4 = (n: number) => n.toString(16).padStart(4, '0').toUpperCase();

/** Deflate (zlib) with the browser's own compressor. */
export async function deflate(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** RGBA pixels (from a canvas) as a lossless PDF image, keeping their transparency when `withAlpha`. */
export async function flateImage(rgba: Uint8Array | Uint8ClampedArray, width: number, height: number, withAlpha = false): Promise<PdfImage> {
  const rgb = new Uint8Array(width * height * 3);
  const alpha = withAlpha ? new Uint8Array(width * height) : undefined;
  for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
    rgb[j] = rgba[i];
    rgb[j + 1] = rgba[i + 1];
    rgb[j + 2] = rgba[i + 2];
    if (alpha) alpha[i / 4] = rgba[i + 3];
  }
  return { width, height, data: await deflate(rgb), filter: 'FlateDecode', ...(alpha ? { alpha: await deflate(alpha) } : {}) };
}

export function jpegImage(jpeg: Uint8Array): PdfImage {
  return { ...jpegSize(jpeg), data: jpeg, filter: 'DCTDecode' };
}

/** A PDF text string that keeps any character: UTF-16BE with a byte-order mark. */
export function pdfText(s: string): string {
  let hex = 'FEFF';
  for (let i = 0; i < s.length; i++) hex += s.charCodeAt(i).toString(16).padStart(4, '0').toUpperCase();
  return `<${hex}>`;
}

/**
 * Text for the built-in Helvetica (WinAnsi), which covers Latin-1. Characters outside it
 * become "?" rather than garbage.
 */
export function winAnsi(s: string): string {
  let out = '';
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    if (ch === '(' || ch === ')' || ch === '\\') out += '\\' + ch;
    else if (c >= 0x20 && c < 0x7f) out += ch;
    else if (c >= 0xa0 && c <= 0xff) out += '\\' + c.toString(8).padStart(3, '0');
    else if (ch === '–' || ch === '—') out += '\\226';
    else if (ch === '’') out += '\\222';
    else out += '?';
  }
  return out;
}

/** Helvetica's average width is about half its size; good enough to center a short line. */
export const approxWidth = (s: string, size: number) => [...s].length * size * 0.5;

export function pdfDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `D:${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`;
}

export function pdfDocument(pages: PdfPage[], title = 'Family chart', created = new Date()): Uint8Array {
  const n = (v: number) => v.toFixed(2);
  const enc = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (part: string | Uint8Array) => {
    const bytes = typeof part === 'string' ? enc.encode(part) : part;
    chunks.push(bytes);
    length += bytes.length;
  };
  /** Objects are numbered in the order they're written. */
  const obj = (body: string | (() => void)) => {
    offsets.push(length);
    push(`${offsets.length} 0 obj\n`);
    if (typeof body === 'string') push(body);
    else body();
    push('\nendobj\n');
    return offsets.length;
  };
  const stream = (dict: string, data: Uint8Array | string) => {
    const bytes = typeof data === 'string' ? enc.encode(data) : data;
    return obj(() => {
      push(`<< ${dict} /Length ${bytes.length} >>\nstream\n`);
      push(bytes);
      push('\nendstream');
    });
  };

  // Every character the text layer uses gets a two-byte code: 1, 2, 3… in order of first use.
  const chars: string[] = [];
  const codeOf = new Map<string, number>();
  for (const pg of pages)
    for (const t of pg.texts ?? [])
      for (const ch of t.text)
        if (!codeOf.has(ch) && chars.length < 0xfffe) {
          chars.push(ch);
          codeOf.set(ch, chars.length);
        }

  // 1: catalog, 2: page tree, 3: footer font; then, when there's text, the text font (font,
  // its glyphs' font, descriptor, font file, code-to-glyph map, Unicode map); then per page its
  // image, contents, and page object.
  const firstPage = chars.length ? 10 : 4;
  const pageIds = pages.map((_, i) => firstPage + i * 3 + 2);
  push('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n');
  obj('<< /Type /Catalog /Pages 2 0 R >>');
  obj(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`);
  obj('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  const textFont = chars.length ? '/T0 4 0 R' : '';
  if (chars.length) {
    const fontName = '/RelationshipsText';
    obj(`<< /Type /Font /Subtype /Type0 /BaseFont ${fontName} /Encoding /Identity-H /DescendantFonts [5 0 R] /ToUnicode 9 0 R >>`);
    obj(`<< /Type /Font /Subtype /CIDFontType2 /BaseFont ${fontName} /CIDSystemInfo << /Registry (Adobe) /Ordering (Identity) /Supplement 0 >> /FontDescriptor 6 0 R /DW ${BLANK_ADVANCE} /CIDToGIDMap 8 0 R >>`);
    obj(`<< /Type /FontDescriptor /FontName ${fontName} /Flags 4 /FontBBox [0 -200 ${BLANK_ADVANCE} 800] /ItalicAngle 0 /Ascent 800 /Descent -200 /CapHeight 700 /StemV 80 /FontFile2 7 0 R >>`);
    const ttf = blankFont();
    stream(`/Length1 ${ttf.length}`, ttf);
    // Every code draws glyph 1, the blank one.
    const map = new Uint8Array((chars.length + 1) * 2);
    for (let i = 1; i <= chars.length; i++) map[i * 2 + 1] = 1;
    stream('', map);
    stream('', toUnicodeCMap(chars.map((ch, i) => [i + 1, ch])));
  }
  if (offsets.length + 1 !== firstPage) throw new Error('PDF objects out of order');
  for (const pg of pages) {
    const im = pg.image;
    const img = stream(`/Type /XObject /Subtype /Image /Width ${im.width} /Height ${im.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /${im.filter}`, im.data);
    let content = `q ${n(pg.w)} 0 0 ${n(pg.h)} ${n(pg.x)} ${n(pg.y)} cm /Im0 Do Q`;
    if (pg.footer) {
      const size = 8;
      content += `\nBT /F1 ${size} Tf 0.45 g ${n((pg.width - approxWidth(pg.footer, size)) / 2)} ${n(Math.min(MARGIN / 2, pg.y / 2))} Td (${winAnsi(pg.footer)}) Tj ET`;
    }
    if (pg.texts?.length) {
      // Invisible (render mode 3), each line stretched (Tz) to span what the picture shows.
      const runs = pg.texts.map((t) => {
        const glyphs = [...t.text].filter((ch) => codeOf.has(ch));
        const stretch = (t.width / Math.max(1, glyphs.length * (BLANK_ADVANCE / 1000) * t.size)) * 100;
        return `${n(stretch)} Tz 1 0 0 1 ${n(t.x)} ${n(t.y)} Tm /T0 ${n(t.size)} Tf <${glyphs.map((ch) => code4(codeOf.get(ch)!)).join('')}> Tj`;
      });
      content += `\nBT 3 Tr\n${runs.join('\n')}\nET`;
    }
    const contents = stream('', content);
    obj(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${n(pg.width)} ${n(pg.height)}] /Resources << /XObject << /Im0 ${img} 0 R >> /Font << /F1 3 0 R ${textFont} >> >> /Contents ${contents} 0 R >>`);
  }
  const info = obj(`<< /Title ${pdfText(title)} /Producer (Relationships) /CreationDate (${pdfDate(created)}) >>`);
  const xref = length;
  push(`xref\n0 ${offsets.length + 1}\n0000000000 65535 f \n${offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')}`);
  push(`trailer\n<< /Size ${offsets.length + 1} /Root 1 0 R /Info ${info} 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

/**
 * Builds a PDF out of numbered objects, which can be reserved ahead (so objects can point at
 * ones written later) and written in any order.
 */
export class PdfWriter {
  private chunks: Uint8Array[] = [];
  private length = 0;
  private offsets: number[] = [];
  private next = 1;
  private enc = new TextEncoder();

  constructor() {
    this.push('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n');
  }

  private push(part: string | Uint8Array) {
    const bytes = typeof part === 'string' ? this.enc.encode(part) : part;
    this.chunks.push(bytes);
    this.length += bytes.length;
  }

  /** A number for an object to be written later. */
  reserve(): number {
    return this.next++;
  }

  /** Writes an object's dictionary (or other value); returns its number. */
  object(body: string, id = this.reserve()): number {
    this.offsets[id] = this.length;
    this.push(`${id} 0 obj\n${body}\nendobj\n`);
    return id;
  }

  /** Writes a stream; `dict` is what goes in its dictionary besides the length. */
  stream(dict: string, data: Uint8Array | string, id = this.reserve()): number {
    const bytes = typeof data === 'string' ? this.enc.encode(data) : data;
    this.offsets[id] = this.length;
    this.push(`${id} 0 obj\n<< ${dict} /Length ${bytes.length} >>\nstream\n`);
    this.push(bytes);
    this.push('\nendstream\nendobj\n');
    return id;
  }

  /** An image (and its transparency, if it has some); returns the image's number. */
  image(im: PdfImage): number {
    const mask = im.alpha ? this.stream(`/Type /XObject /Subtype /Image /Width ${im.width} /Height ${im.height} /ColorSpace /DeviceGray /BitsPerComponent 8 /Filter /FlateDecode`, im.alpha) : 0;
    return this.stream(`/Type /XObject /Subtype /Image /Width ${im.width} /Height ${im.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /${im.filter}${mask ? ` /SMask ${mask} 0 R` : ''}`, im.data);
  }

  finish(root: number, info: number): Uint8Array {
    const count = this.next;
    for (let id = 1; id < count; id++) if (this.offsets[id] === undefined) throw new Error(`PDF object ${id} was never written`);
    const xref = this.length;
    this.push(`xref\n0 ${count}\n0000000000 65535 f \n${this.offsets.slice(1).map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')}`);
    this.push(`trailer\n<< /Size ${count} /Root ${root} 0 R /Info ${info} 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
    const out = new Uint8Array(this.length);
    let at = 0;
    for (const c of this.chunks) {
      out.set(c, at);
      at += c.length;
    }
    return out;
  }
}

